import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { V2Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  LineOfBusiness,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { LawwSignInAlert } from '../../common/GuestHeader/LawwSignInAlert';
import { setGeoLocationToStorage } from '../../common/PSXHeader/SearchBar/utils';
import { PSXIcon } from '../../common/PSXIcon';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import GuestCoachmark from './common/GuestCoachmark';
import { InformationalDrawer } from './common/InformationalDrawer';
import { getFilteredGuestLobOptions } from './GuestLineOfBusinessPicker.utils';
import { toSlug } from './Guest.utils';
import { GuestFirstTimeUserModal } from './GuestFirstTimeUserModal';
import { InfoButtonStyle, StyleForHeader } from './GuestPlanSelection.style';
import { getBlueGreenGQLApiUrl, isLawwGuest } from './utils';

const LineOfBusinessStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '180px',
    height: '343px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
});

type LineOfBusinessCardProps = {
  title: string;
  analyticsTitle: string;
  description: string;
  icon: string;
  linkText: string;
  lob: string;
};

const LineOfBusinessCard = ({
  title,
  analyticsTitle,
  description,
  icon,
  linkText,
  lob,
}: LineOfBusinessCardProps) => {
  const { i18n } = useTranslation();
  const { navigate } = useRouter();
  let url = '/select-coverage-type?planSelectionLob=' + lob;
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;
  const coverageLinks = useLagoon('coverage-links')();

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );

  const { setIsNavigatingMaestro, clearRequestData } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      setIsNavigatingMaestro: state.setIsNavigatingMaestro,
      clearRequestData: state.clearRequestData,
    }))
  );

  const { coverageType, isGuestDeeplinkFlow } = guestPlanSelectorData || {};
  const cardWidth = i18n.language?.toLowerCase().startsWith('es')
    ? '189px'
    : '180px';

  const isLawwGuestExperience = isLawwGuest();
  const handleClick = () => {
    setGeoLocationToStorage({});
    sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
    setIsNavigatingMaestro(false);
    clearRequestData();
    adobeLinkTrackEvent({
      name: linkText,
      location: analyticsTitle,
      type: 'internal',
      destinationUrl: url,
    });

    if (isGuestDeeplinkFlow && coverageType) {
      const matchedCoverageLink = coverageLinks.find(
        (item) => item?.coverage === coverageType && item?.lob === lob
      )?.link;

      if (matchedCoverageLink) {
        window.open(matchedCoverageLink, '_blank');
        return;
      }

      // Special case: CNS with VISION coverage does not change the URL (business requirement)
      url =
        lob === LineOfBusiness.CNS &&
        coverageType === ReverseCoverageTypesCodes.VISION
          ? url
          : `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`;
    }

    if (isLawwGuestExperience) {
      url = `/plan-selection?planSelectionLob=${lob}&coverageType=B&chipValue=Behavioral%20Health`;
    }
    navigate(url);
  };

  return (
    <LineOfBusinessStyledCard onClick={handleClick}>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '0px',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '2px',
              alignItems: 'start',
            },
          }}
        >
          {/* Note: The health_access icon is not available in Abyss for Optum branding hence created a custom svg */}
          {icon === 'health_access_optum_custom_svg' ? (
            <PSXIcon icon="health_access_optum_custom_svg" />
          ) : (
            <IconBrand icon={icon} size="48px" />
          )}
          <Heading offset={6}>{title}</Heading>
          <Text
            css={{
              'abyss-text-root': {
                display: 'inline-block',
                lineHeight: '1.3 !important',
              },
            }}
            size={'$sm'}
          >
            {description}
          </Text>
        </Layout.Stack>
      </Card.Section>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '16px',
            height: '52px',
            width: cardWidth,
            marginLeft: '0px',
            marginRight: '0px',
          },
        }}
      >
        <Layout.Group>
          <Link
            after={<IconSymbol icon="chevron_right" />}
            data-testid={`explore-${toSlug(lob || '')}-link`}
            isStandardAnchor
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            size={'14px !important'}
          >
            {linkText}
          </Link>
        </Layout.Group>
      </Card.Section>
    </LineOfBusinessStyledCard>
  );
};

export const GuestLineOfBusinessPicker = () => {
  const { t } = useTranslation();
  const { chooseType, guestData, guestPlanSelectorData, setChooseType } =
    useGuestStore(
    useShallow((state: GuestState) => ({
      chooseType: state.chooseType,
      guestData: state.guestData,
      guestPlanSelectorData: state.guestPlanSelectorData,
      setChooseType: state.setChooseType,
    }))
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lobs = useLagoon('lob')();
  const { coverageType, isGuestDeeplinkFlow } = guestPlanSelectorData || {};

  const [
    isFirstTimeUserBannerEnabled,
    enableUpdatedPSXBannerToSignIn,
    isDisableStandaloneMedicaid,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_UPDATED_PSX_BANNER_TO_SIGN_IN,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_STAND_ALONE_MEDICAID,
  ]);

  const isLawwGuestExperience = isLawwGuest();
  lobs?.sort((lob) => lob.displayOrder);

  const filteredLobs = getFilteredGuestLobOptions({
    lobs,
    isGuestDeeplinkFlow:
      Boolean(isGuestDeeplinkFlow) && isDisableStandaloneMedicaid,
    coverageType,
    lineOfBusiness: guestData?.lob,
  });

  const lagoonUIMessage = useLagoon('ui-messaging')();

  const chooseMedicalCoverageType = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICAL_COVERAGE_TYPE,
  })?.message;
  const typeOfMedicalCoverageHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE_HEADING,
  })?.message;
  const getMoreCoverageTypesInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.GET_MORE_COVERAGE_TYPE_INFO,
  })?.message;
  const getMoreCoverageTypesInfoButtonText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .GET_MORE_COVERAGE_TYPE_INFO_BUTTON_TEXT,
  })?.message;
  const exploreCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.EXPLORE_COVERAGE,
  })?.message;

  const typeOfMedicalCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE,
  })?.message;

  const coachmarkContentText =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_COACHMARK_CONTENT,
    })?.message || '';

  const learnMoreButtonText =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.LEARN_MORE_BUTTON_TEXT,
    })?.message || '';

  const bannerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .LOB_PICKER_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message;
  // Retrieve the sign-in URL saved by GuestHeader
  const signInUrl = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_SIGN_IN_URL
  );

  const drawerHeaderText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_HEADER,
  })?.message;

  const drawerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_CONTENT,
  })?.message;
  const drawerHeaderCoverageInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .COVERAGE_TYPE_INFORMATION_DRAWER_HEADER,
  })?.message;
  const drawerContentCoverageInfoText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .COVERAGE_TYPE_INFORMATION_DRAWER_CONTENT,
  })?.message;
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const flags = useFlags();
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({
    url: guestBGApiUrl,
  });

  const handleOnLearnMoreClick = () => {
    setIsDrawerOpen(true);
  };

  const handleOnClose = () => {
    setEducationalContentStore({
      ...educationalContentStore,
      hasViewedGuestLobBanner: true,
    });

    setEducationalContentStatus({
      variables: {
        ...educationalContentStore,
        hasViewedContent: educationalContentStore?.hasViewedContent ?? true,
        hasViewedGuestLobBanner: true,
      },
    });
  };

  useEffect(() => {
    if (chooseMedicalCoverageType) {
      setChooseType(chooseMedicalCoverageType);
    }
  }, [chooseMedicalCoverageType, setChooseType]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: `guest plan selection`,
    isGuest: true,
  });

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  const infoButtonAriaLabel = t(
    'MORE_INFORMATION_ABOUT_VARIOUS_MEDICAL_COVERAGES'
  );

  const handleInfoButtonFocus = (
    event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    event.target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const showFirstTimeUserBanner =
    isFirstTimeUserBannerEnabled &&
    educationalContentStore &&
    !educationalContentStore?.hasViewedGuestLobBanner;

  return (
    <>
      <GuestFirstTimeUserModal />
      <Box color="$bgHeaderColor" css={StyleForHeader}>
        <Flex css={{ width: '100%', justifyContent: 'center' }}>
          <Flex
            css={{ flexDirection: 'column', maxWidth: '1200px', width: '100%' }}
          >
            {typeOfMedicalCoverageText}
            <div style={{ position: 'relative' }}>
              <div
                id="coachmark-anchor"
                style={{ display: 'inline-block', maxWidth: '100%' }}
              >
                <Heading css={{ marginTop: '8px' }} offset={1}>
                  {chooseType}
                </Heading>
              </div>
              {showFirstTimeUserBanner && enableUpdatedPSXBannerToSignIn && (
                <GuestCoachmark
                  content={coachmarkContentText}
                  learnMoreButtonText={learnMoreButtonText}
                  onClose={handleOnClose}
                  onLearnMore={handleOnLearnMoreClick}
                  position="right"
                  target="#coachmark-anchor"
                  title={chooseType}
                />
              )}
            </div>
          </Flex>
        </Flex>
      </Box>
      {showFirstTimeUserBanner && !enableUpdatedPSXBannerToSignIn && (
        <FirstTimeUserBanner
          content={bannerContentText}
          onClose={handleOnClose}
          onLearnMoreClick={handleOnLearnMoreClick}
          showLearnMoreButton={true}
        />
      )}
      {isLawwGuestExperience && <LawwSignInAlert />}
      <PageBody
        css={{
          marginBottom: '8px',
          padding: '32px 24px 24px 24px',
        }}
      >
        <Flex
          alignContent="center"
          css={{ gap: '6px' }}
          direction="row"
          justify="center"
        >
          <Heading data-testid="lob-selection" offset={3} textAlign="center">
            {typeOfMedicalCoverageHeadingText}
          </Heading>
          {!isLawwGuestExperience && (
            <V2Button
              aria-label={infoButtonAriaLabel}
              css={InfoButtonStyle}
              data-testid="info-button"
              onClick={() => setIsDrawerOpen(true)}
              onFocus={handleInfoButtonFocus}
              style={{ alignSelf: 'center' }}
              variant="text"
            >
              <IconSymbol icon="info" size={16} variant="outlined" />
            </V2Button>
          )}
        </Flex>
        {isLawwGuestExperience && (
          <Flex
            alignItems="baseline"
            css={{ marginTop: '8px' }}
            direction="column"
            justify="center"
          >
            <Text css={{ alignSelf: 'center', textAlign: 'center' }}>
              {getMoreCoverageTypesInfoText}
            </Text>
            <V2Button
              aria-label={infoButtonAriaLabel}
              css={InfoButtonStyle}
              data-testid="info-button"
              onClick={() => setIsDrawerOpen(true)}
              onFocus={handleInfoButtonFocus}
              style={{ alignSelf: 'center' }}
              variant="text"
            >
              {getMoreCoverageTypesInfoButtonText}
            </V2Button>
          </Flex>
        )}

        {!isLawwGuestExperience && !enableUpdatedPSXBannerToSignIn && (
          <Flex
            alignItems="baseline"
            css={{ marginTop: '8px' }}
            direction="row"
            justify="center"
          >
            <Text css={{ alignSelf: 'center', textAlign: 'center' }}>
              {signInMessageText}
            </Text>
            <Link
              data-testid="sign-in-link"
              href={signInUrl || ''}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: signInText,
                  location: chooseType,
                  type: 'internal',
                  destinationUrl: signInUrl,
                });
              }}
              onFocus={handleInfoButtonFocus}
              openNewWindow={false}
              style={{ marginLeft: '4px' }}
            >
              {signInText}
            </Link>
          </Flex>
        )}

        <Flex
          alignItems="baseline"
          direction="row"
          justify="center"
          style={{ gap: '24px', marginTop: '32px' }}
        >
          {filteredLobs.map((lob) => (
            <LineOfBusinessCard
              analyticsTitle={lob.dnt_name}
              description={lob.description}
              icon={lob.dntIcon}
              key={lob.key}
              linkText={exploreCoverageText}
              lob={lob.key}
              title={lob.displayName}
            />
          ))}
        </Flex>
        <InformationalDrawer
          content={
            isLawwGuestExperience
              ? drawerContentCoverageInfoText
              : drawerContentText
          }
          header={
            isLawwGuestExperience
              ? drawerHeaderCoverageInfoText
              : drawerHeaderText
          }
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      </PageBody>
    </>
  );
};
